import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Api } from "../../api.js";
import { BaseElement } from "../../utils/ui-components.js";
import { userCache } from "./user-cache.js";
import { until } from "lit/directives/until.js";
import { deleteIcon } from "../../utils/icons.js";

@customElement("invitation-list")
export class InvitationList extends BaseElement {
    @state()
    private invitations: { id: string; code: string; created_by: string; created_at: Date }[] = [];

    @state()
    private loading = true;

    async firstUpdated() {
        await this.loadInvitations();
    }

    private async loadInvitations() {
        try {
            const result = await Api.getValidInvitations();
            this.invitations = result.invitations;
        } catch (e) {
            console.error("Failed to load invitations:", e);
        } finally {
            this.loading = false;
        }
    }

    private async createInvitation() {
        try {
            const result = await Api.createInvitation();
            const validInvitations = await Api.getValidInvitations();
            this.invitations = validInvitations.invitations;
        } catch (e) {
            console.error("Failed to create invitation:", e);
        }
    }

    render() {
        if (this.loading) {
            return html`<div class="p-4">Loading...</div>`;
        }

        return html`
            <div class="bg-white rounded-lg shadow">
                <div class="p-4 border-b border-gray-200">
                    <div class="flex justify-between items-center">
                        <h2 class="text-lg font-semibold">Invitation Codes</h2>
                        <button @click=${this.createInvitation} class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                            New Invitation
                        </button>
                    </div>
                </div>
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="pl-4 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th class="pr-4 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            ${this.invitations.map(
                                (invitation) => html`
                                    <tr>
                                        <td class="pl-4 px-2 py-4 whitespace-nowrap text-sm font-mono">
                                            <a href="/register?code=${invitation.code}" class="text-blue-600 hover:text-blue-800">
                                                ${invitation.code.slice(0, 6)}...
                                            </a>
                                        </td>
                                        <td class="px-2 py-4 whitespace-nowrap text-sm">
                                            ${until(userCache.getUserName(invitation.created_by), "Loading...")}
                                        </td>
                                        <td class="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                            ${new Date(invitation.created_at).toLocaleDateString()}
                                        </td>
                                        <td class="pr-4 px-2 py-4 whitespace-nowrap text-sm">
                                            <button
                                                @click=${() => this.deleteInvitation(invitation.id)}
                                                class="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50"
                                                title="Delete invitation"
                                            >
                                                <i class="icon w-4 h-4">${deleteIcon}</i>
                                            </button>
                                        </td>
                                    </tr>
                                `
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    private async deleteInvitation(id: string) {
        try {
            await Api.deleteInvitation(id);
            this.invitations = this.invitations.filter((inv) => inv.id !== id);
        } catch (e) {
            console.error("Failed to delete invitation:", e);
        }
    }
}
